import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

/**
 * Cart contents live in localStorage, mirroring the prototype (cartArr :1254,
 * bookArr :5028).
 *
 * This is safe ONLY because the cart holds slugs and quantities — never prices.
 * Every total shown comes from POST /cart/quote and every charge is recomputed
 * by POST /orders, so a tampered localStorage can change WHAT you try to buy,
 * never WHAT IT COSTS. (An access token here would be an XSS jackpot; a list of
 * slugs is not.)
 */

const COURSE_KEY = 'maktech_cart';
const BOOK_KEY = 'maktech_books';

export interface BookLine {
  slug: string;
  qty: number;
}

interface CartState {
  courses: string[];
  books: BookLine[];
  addCourse: (slug: string) => void;
  removeCourse: (slug: string) => void;
  hasCourse: (slug: string) => boolean;
  addBook: (slug: string) => void;
  setBookQty: (slug: string, delta: number) => void;
  clearCourses: () => void;
  clearBooks: () => void;
  count: number;
}

const Ctx = createContext<CartState | null>(null);

function read<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, val: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(val));
  } catch {
    /* quota / private mode — the cart is not worth crashing the page over */
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [courses, setCourses] = useState<string[]>(() => read<string[]>(COURSE_KEY, []));
  const [books, setBooks] = useState<BookLine[]>(() => read<BookLine[]>(BOOK_KEY, []));

  useEffect(() => write(COURSE_KEY, courses), [courses]);
  useEffect(() => write(BOOK_KEY, books), [books]);

  const addCourse = useCallback((slug: string) => {
    setCourses((c) => (c.includes(slug) ? c : [...c, slug]));
  }, []);

  const removeCourse = useCallback((slug: string) => {
    setCourses((c) => c.filter((x) => x !== slug));
  }, []);

  const hasCourse = useCallback((slug: string) => courses.includes(slug), [courses]);

  const addBook = useCallback((slug: string) => {
    setBooks((b) => {
      const line = b.find((x) => x.slug === slug);
      if (line) return b.map((x) => (x.slug === slug ? { ...x, qty: Math.min(x.qty + 1, 20) } : x));
      return [...b, { slug, qty: 1 }];
    });
  }, []);

  /** setBookQty (:5059) — dropping below 1 removes the line. */
  const setBookQty = useCallback((slug: string, delta: number) => {
    setBooks((b) =>
      b
        .map((x) => (x.slug === slug ? { ...x, qty: Math.min(x.qty + delta, 20) } : x))
        .filter((x) => x.qty >= 1),
    );
  }, []);

  const clearCourses = useCallback(() => setCourses([]), []);
  const clearBooks = useCallback(() => setBooks([]), []);

  const value = useMemo<CartState>(
    () => ({
      courses,
      books,
      addCourse,
      removeCourse,
      hasCourse,
      addBook,
      setBookQty,
      clearCourses,
      clearBooks,
      count: courses.length + books.reduce((s, b) => s + b.qty, 0),
    }),
    [courses, books, addCourse, removeCourse, hasCourse, addBook, setBookQty, clearCourses, clearBooks],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCart(): CartState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useCart must be used inside <CartProvider>');
  return v;
}
